// Betekenisvolle kleuren en mijlpalen voor het Dagstart-rooster (Joris, 7 okt).
//
// Kleur: tot nu toe kreeg elk projectblok een pastel uit een hash van het project-id
// (rooster.js → projectKleur): zelfde project = zelfde kleur, maar wát een kleur betekende
// was niet te zien. Hier is kleur één as: de SOORT WERK (script/storyboard, animatie,
// montage, draaidag, intern/overig, afspraak), gedempt, met een legenda bovenaan.
//
// Mijlpalen: het ruitje (◆) staat aan het EINDE van de taak waar het bij hoort, met de
// versie van díe taak er direct vóór ("v intern ◆"). Voorheen stond het ruitje op de grens
// tussen twee taken en begon het label van de vólgende taak er direct achter ("◆ v1"), zodat
// de deadline van v intern las als die van v1. v1 is de hoofdmijlpaal en krijgt een accent.
//
// Puur (geen React): getest in utils/__tests__/roosterBetekenis.test.js.

import {
  segmentLabels, onderscheidendeLabels, kortTaak, kolomBereik, dagDeel, tint, DRAAIDAG_ORANJE, PASTEL_TEKST,
} from '../pages/PlannerTodo/rooster';

// ─── Soort werk ──────────────────────────────────────────────────────────────

/** Accent voor de hoofdmijlpaal (v1): het enige verzadigde element op het rooster. */
export const V1_ACCENT = '#4338ca';
/**
 * Ruitje en label van de overige versies (o.a. "v intern"). Was #8b93a1; Joris (3 nov):
 * "de V-intern mag iets aanweziger" — donkerder slate, nog steeds stiller dan het v1-accent.
 */
export const MIJLPAAL_GEDEMPT = '#5b6474';

/** Dekking van het vlak van een soort werk (was 20 %; 3 nov: "de kleuren mag iets harder"). */
export const VLAK_DEKKING = 0.28;

/**
 * De soorten werk, in de volgorde van de legenda. `kleur` is gedempt maar iets verzadigder
 * dan eerst (3 nov), zodat de soorten beter uit elkaar te houden zijn; het vlak is die kleur
 * op VLAK_DEKKING, de streep links de kleur zelf. Tekst blijft PASTEL_TEKST (donker, AA).
 * Intern & overig is een eigen slate-tint: aanweziger dan het bijna-witte vlak van voorheen,
 * maar ondergeschikt aan projectwerk.
 */
export const WERKSOORTEN = [
  { key: 'voorbereiding', label: 'Script & storyboard', kleur: '#7a62c6' },
  { key: 'animatie', label: 'Animatie & design', kleur: '#2c8f6c' },
  { key: 'montage', label: 'Montage & afwerking', kleur: '#3d72b8' },
  { key: 'draaidag', label: 'Draaidag', kleur: DRAAIDAG_ORANJE, vlak: tint(DRAAIDAG_ORANJE, 0.2) },
  { key: 'overig', label: 'Intern & overig', kleur: '#6b7689', vlak: '#e3e7ed' },
  { key: 'afspraak', label: 'Afspraak', kleur: '#b8bec8', vlak: '#e5e7eb' },
];
const PER_KEY = Object.fromEntries(WERKSOORTEN.map((s) => [s.key, s]));

// Volgorde telt: "Storyboard animatie" is voorbereiding, "Montage animatiefilm" montage.
const TREFWOORDEN = [
  ['voorbereiding', /\b(script|scripts|storyboard|storyboards|concept|moodboard|treatment|scenario|draaiboek|briefing|research|voorbereiding|preproductie|shotlist|interview ?vragen)/],
  ['montage', /\b(montage|edit|editen|edits|kleurcorrectie|grading|grade|afmix|afmixen|mix|geluid|audio|sound|ondertitel|ondertiteling|subtitles?|export|render)/],
  ['animatie', /\b(animatie|animaties|animation|animeren|motion|illustratie|illustraties|illustration|design|ontwerp|vormgeving|styleframes?|character|infographic|2d|3d|after effects|lottie)/],
];

const isDraaidag = (t) => t?.type === 'Draaidag' || (t?.prio || t?.status) === 'Draaidag' || !!t?.bronDraaidag;
const isAfspraak = (t) => !!t?.bronAgenda || t?.type === 'Afspraak';
const isIntern = (t) => {
  const s = [t?.type, t?.prio, t?.status].map((x) => String(x || '').toLowerCase());
  if (s.some((x) => x === 'vrij/intern' || x === 'intern')) return true;
  const project = String(t?.projectName || t?.project || '').toLowerCase();
  return /\bfull ?frame\b|\bintern\b/.test(project);
};

/** Soort werk van één taak: key uit WERKSOORTEN. */
export function werkSoort(task) {
  if (!task) return 'overig';
  if (isDraaidag(task)) return 'draaidag';
  if (isAfspraak(task)) return 'afspraak';
  if (isIntern(task)) return 'overig';
  // Eerst het korte taaklabel ("Montage v1" uit "Montage v1 - Animatiefilm"), dan de rest.
  const kort = kortTaak(task.name).toLowerCase();
  const heel = String(task.name || '').toLowerCase();
  const soortIn = (tekst) => TREFWOORDEN.find(([, re]) => re.test(tekst))?.[0] || null;
  return soortIn(kort) || soortIn(heel) || soortIn(String(task.type || '').toLowerCase()) || 'overig';
}

/**
 * Soort werk van een blok met meerdere taken: de soort met de meeste uren (taken zonder uren
 * tellen als 1); bij gelijkspel de eerste. Script 10 u + animatie 16 u = een animatieblok.
 */
export function blokWerkSoort(tasks) {
  const tel = new Map();
  (tasks || []).forEach((t) => {
    const s = werkSoort(t);
    tel.set(s, (tel.get(s) || 0) + (Number(t?.hours) > 0 ? Number(t.hours) : 1));
  });
  let beste = 'overig';
  let max = 0;
  tel.forEach((n, s) => { if (n > max) { max = n; beste = s; } });
  return beste;
}

/**
 * Stijl van een blok in de Dagstart: zelfde vorm als projectStijl (vlak, streep, tekst),
 * maar de kleur volgt de soort werk.
 * @returns {{ vlak: string, streep: string, tekst: string, soort: string }}
 */
export function werkSoortStijl(tasksOfTask) {
  const tasks = Array.isArray(tasksOfTask) ? tasksOfTask : [tasksOfTask];
  const soort = blokWerkSoort(tasks);
  const s = PER_KEY[soort] || PER_KEY.overig;
  return { vlak: s.vlak || tint(s.kleur, VLAK_DEKKING), streep: s.kleur, tekst: PASTEL_TEKST, soort };
}

// ─── Versies en mijlpalen ────────────────────────────────────────────────────

/**
 * Versie uit een taaknaam: "v intern", "v1", "v2", …, "DEF" — of null.
 * Kijkt naar het korte taaklabel (vóór " - "), zodat een filmtitel geen versie wordt.
 */
export function versieVan(naam) {
  const kort = kortTaak(naam).toLowerCase();
  if (/\bv[\s.-]*intern\b|\bversie[\s.-]*intern\b/.test(kort)) return 'v intern';
  // Volledig versietoken: "v1.2" is een subversie, geen v1 (en dus geen hoofdmijlpaal).
  const nr = /\b(?:v|versie)[\s.-]*(\d{1,2}(?:\.\d{1,2})?)(?![\d.])/.exec(kort);
  if (nr) return nr[1].includes('.') ? `v${nr[1]}` : `v${Number(nr[1])}`;
  if (/\bdef\b|\bdefinitie(?:f|ve)\b|\bfinal\b/.test(kort)) return 'DEF';
  return null;
}

/**
 * Versie uit een taaknaam, ook als die met het projectnummer begint ("9255 - OMWB V1"): dan telt
 * het deel na het nummer. Zelfde regel als de engine (api/frontdoor/dagstart.versie_rang).
 */
export function versieVanNaam(naam) {
  const n = String(naam || '');
  const zonderNummer = n.replace(/^\s*\d{4,5}\s+-\s+/, '');
  return versieVan(n) || (zonderNummer !== n ? versieVan(zonderNummer) : null);
}

/** Is dit de hoofdmijlpaal (v1, de eerste versie naar de klant)? */
export const isHoofdMijlpaal = (versie) => versie === 'v1';

/** DEF: de definitieve versie — eigen, donkere badge (Joris, 3 nov: "DEF mag veel duidelijker"). */
export const DEF_ACCENT = '#111827';
/** v2, v3, …: ook een gevulde badge, rustiger dan v1 en DEF. */
export const VERVOLG_ACCENT = '#475569';
/** v intern (3 nov: "v intern, v1, v2 en DEF moeten erin"): eigen gevulde badge, teal. */
export const INTERN_ACCENT = '#0f766e';

/**
 * Badge voor een mijlpaal-label ("v1", "v2", "DEF", "v1 + DEF"), of null (v intern, subversies,
 * geen versie). Bij meer versies in één stuk wint DEF, dan v1, dan een vervolgversie.
 * @returns {{ soort: 'def'|'v1'|'vervolg', kleur: string } | null}
 */
export function mijlpaalBadge(versie) {
  const delen = String(versie || '').split(' + ').filter(Boolean);
  if (delen.includes('DEF')) return { soort: 'def', kleur: DEF_ACCENT };
  if (delen.some(isHoofdMijlpaal)) return { soort: 'v1', kleur: V1_ACCENT };
  if (delen.some((v) => /^v\d+$/.test(v))) return { soort: 'vervolg', kleur: VERVOLG_ACCENT };
  if (delen.includes('v intern')) return { soort: 'intern', kleur: INTERN_ACCENT };
  return null;
}

/** Patroon voor één versie-token ("v1", "v1.2", "v intern", "DEF") in een label. */
function versieToken(versie) {
  if (versie === 'v intern') return /(?<![a-z0-9])v(?:ersie)?[\s.-]*intern(?![a-z0-9])/i;
  if (versie === 'DEF') return /(?<![a-z0-9])(?:def|definitie(?:f|ve)|final)(?![a-z0-9])/i;
  const m = /^v(\d+)(?:\.(\d+))?$/.exec(String(versie || ''));
  if (!m) return null;
  return new RegExp(`(?<![a-z0-9])v(?:ersie)?[\\s.-]*0*${m[1]}${m[2] ? `[.,]0*${m[2]}` : ''}(?![\\d.])`, 'i');
}

/**
 * Het label van een mijlpaal zonder de taakversie: "OMWB · V1" (v1) → "OMWB", "v1" → "". Alleen de
 * eerste keer dat elke versie van díe taak voorkomt gaat eruit; versiewoorden in een filmtitel
 * blijven staan ("v1 · Apollo v2" bij v1 → "Apollo v2"; Codex 3 nov).
 */
export function mijlpaalNaam(label, versie) {
  let tekst = String(label || '');
  String(versie || '').split(' + ').filter(Boolean).forEach((v) => {
    const re = versieToken(v);
    if (re) tekst = tekst.replace(re, ' ');
  });
  return tekst
    .replace(/^[\s·\-–+]+|[\s·\-–+]+$/g, '')
    .replace(/\s*·\s*·\s*/g, ' · ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Volgorde van versies: v intern < v1 < v1.2 < v2 < … < DEF; onbekend = null. */
export function versieRang(versie) {
  if (!versie) return null;
  if (versie === 'v intern') return 0;
  if (versie === 'DEF') return 1000;
  const m = /^v(\d+)(?:\.(\d+))?$/.exec(versie);
  return m ? Number(m[1]) + (m[2] ? Number(m[2]) / 100 : 0) : null;
}

const werkSleutel = (t) => String(t?.deliverableId || t?.projectNumber || t?.projectId || '');

/**
 * "Klant reviewt"-perioden op een persoonsrij (Joris, 3 nov): tussen het einde van een versie en
 * de start van de volgende versie van hetzelfde werk (zelfde deliverable, anders projectnummer)
 * reviewt de klant. De start van de volgende taak = het moment dat de feedback binnen is; is de
 * feedback eerder als compleet aangevinkt (feedbackCompleetOp op de volgende taak), dan stopt de
 * arcering daar. Alleen werkdagen in beeld; telt niet mee in de capaciteit.
 *
 * @param blokken   geplaatste blokken van één rij ({ lane, tasks, vast })
 * @param kolommen  de kolommen in beeld ({ iso, weekend })
 * @returns [{ c0, c1, lane, van, naar, tot, compleet, sleutel }]
 */
export function reviewSegmenten(blokken, kolommen) {
  const laneVan = new Map();
  const taken = [];
  // Bezette kolommen per lane: een review tekent nooit onder een ander blok (Codex r1 #6).
  const bezet = new Map();
  (blokken || []).forEach((b) => {
    if (!bezet.has(b.lane)) bezet.set(b.lane, new Set());
    for (let i = b.c0; i <= b.c1; i += 1) bezet.get(b.lane).add(i);
  });
  (blokken || []).forEach((b) => {
    if (b?.vast) return;
    (b.tasks || []).forEach((t) => {
      const versie = versieVanNaam(t?.name);
      if (versieRang(versie) === null || !werkSleutel(t) || !t.startDate) return;
      laneVan.set(t.id, b.lane);
      taken.push({ t, versie, rang: versieRang(versie), sleutel: werkSleutel(t) });
    });
  });
  const perWerk = new Map();
  taken.forEach((x) => {
    if (!perWerk.has(x.sleutel)) perWerk.set(x.sleutel, []);
    perWerk.get(x.sleutel).push(x);
  });
  const uit = [];
  const voegToe = ({ eindA, b, vanVersie, lane, sleutel }) => {
    const startB = dagDeel(b.t.startDate);
    const compleet = b.t.feedbackCompleetOp ? dagDeel(b.t.feedbackCompleetOp) : '';
    const grens = compleet && compleet < startB ? compleet : startB;
    const vrij = bezet.get(lane) || new Set();
    const idx = (kolommen || []).map((k, n) => (
      (!k.weekend && k.iso > eindA && k.iso < grens && !vrij.has(n)) ? n : -1)).filter((n) => n >= 0);
    // Aaneengesloten stukken: een weekend of een ander blok ertussen breekt de arcering (Codex r1 #7).
    let start = 0;
    for (let i = 1; i <= idx.length; i += 1) {
      if (i === idx.length || idx[i] !== idx[i - 1] + 1) {
        if (idx.length) {
          uit.push({
            c0: idx[start], c1: idx[i - 1], lane, van: vanVersie, naar: b.versie, tot: startB,
            compleet: compleet || null, sleutel: `${sleutel}:${idx[start]}`, taak: b.t,
          });
        }
        start = i;
      }
    }
  };
  perWerk.forEach((lijst, sleutel) => {
    lijst.sort((a, b) => dagDeel(a.t.startDate).localeCompare(dagDeel(b.t.startDate)) || a.rang - b.rang);
    lijst.forEach((b, i) => {
      const a = i > 0 && b.rang > lijst[i - 1].rang ? lijst[i - 1] : null;
      const eindZichtbaar = a ? dagDeel(a.t.endDate || a.t.startDate) : '';
      const vorige = b.t.vorigeVersie?.eind ? b.t.vorigeVersie : null;
      // De echte voorganger is wat het laatst eindigt: een afgeronde v2 (uit de engine) gaat vóór
      // een oudere v1 die nog in beeld staat (Codex r2 #5).
      if (vorige && dagDeel(vorige.eind) > eindZichtbaar) {
        voegToe({ eindA: dagDeel(vorige.eind), b, vanVersie: vorige.versie || 'vorige versie',
          lane: laneVan.get(b.t.id) ?? 0, sleutel: `${sleutel}:vorige:${b.t.id}` });
      } else if (a) {
        voegToe({ eindA: eindZichtbaar, b, vanVersie: a.versie,
          lane: laneVan.get(a.t.id) ?? laneVan.get(b.t.id) ?? 0, sleutel: `${sleutel}:${a.t.id}:${b.t.id}` });
      }
    });
  });
  return uit;
}

/**
 * De mijlpalen van een projectblok in de Dagstart, als stukken onder de balk. Elk stuk
 * loopt tot de einddag van zijn taak/taken; het ruitje staat aan het EINDE van dat stuk
 * (= de deadline van díe taak) en het label ervóór is de versie van díe taak.
 *
 * Gegroepeerd op de ECHTE einddatum (niet op de zichtbare kolom): een v1 die binnen het
 * venster af is en een v2 die erna af is, worden nooit één "v1 + v2" — v1 houdt zijn ruitje.
 * - Meerdere einddagen: één stuk per einddag in beeld, elk met een ruitje (ook het laatste).
 * - Taken die na het venster af zijn: één laatste stuk zonder ruitje (deadline buiten beeld),
 *   alleen als er ruimte voor is (anders vallen ze weg: de balk loopt dan al afgekapt door).
 * - Eén einddag in beeld: alleen een stuk als er een versie in de naam staat — een gewone taak
 *   zonder versie heeft genoeg aan het einde van zijn balk.
 *
 * @returns [{ c0, c1, label, eind, versie, hoofd, ruit, taken, deadlines: [{ naam, eind }] }]
 */
export function mijlpaalSegmenten(blok, kolommen) {
  const taken = blok?.tasks || [];
  if (!taken.length || blok.vast) return [];
  // Venstergrens = de laatste kolom van het venster (ook een weekendkolom), niet het einde
  // van de balk: een deadline op zaterdag ligt nog in beeld, ook al eindigt de balk op vrijdag.
  const laatsteIso = kolommen?.length ? kolommen[kolommen.length - 1].iso : '';
  const eindIso = (t) => dagDeel(t.endDate || t.startDate);
  // Korte, onderscheidende labels per taak (zelfde regels als blokSegmenten).
  const labels = segmentLabels(taken.map((t) => t.name));

  const binnen = new Map(); // kolom → { idx: [], eind }
  const buiten = { idx: [], eind: '' };
  taken.forEach((t, i) => {
    const eind = eindIso(t);
    if (laatsteIso && eind > laatsteIso) {
      buiten.idx.push(i);
      if (eind > buiten.eind) buiten.eind = eind;
      return;
    }
    const b = kolomBereik(t.startDate, t.endDate, kolommen);
    const c1 = b ? Math.min(Math.max(b.c1, blok.c0), blok.c1) : blok.c1;
    if (!binnen.has(c1)) binnen.set(c1, { idx: [], eind: '' });
    const g = binnen.get(c1);
    g.idx.push(i);
    if (eind > g.eind) g.eind = eind;
  });
  const groepen = [...binnen.keys()].sort((a, b) => a - b).map((c1) => ({ c1, ...binnen.get(c1), ruit: true }));
  const laatsteBinnen = groepen.length ? groepen[groepen.length - 1].c1 : blok.c0 - 1;
  if (buiten.idx.length && laatsteBinnen < blok.c1) groepen.push({ c1: blok.c1, ...buiten, ruit: false });
  if (!groepen.length) return [];

  // Gelijke labels in verschillende stukken onderscheidend maken.
  const labelVan = (g) => [...new Set(g.idx.map((i) => labels[i]).filter(Boolean))].join(' + ');
  const tel = new Map();
  groepen.forEach((g) => { const l = labelVan(g).toLowerCase(); tel.set(l, (tel.get(l) || 0) + 1); });
  const botsend = groepen.filter((g) => tel.get(labelVan(g).toLowerCase()) > 1).flatMap((g) => g.idx);
  if (botsend.length) {
    const beter = onderscheidendeLabels(botsend.map((i) => taken[i].name), botsend.map((i) => labels[i]));
    botsend.forEach((i, n) => { labels[i] = beter[n]; });
  }

  // Versie uit de taaknaam, anders uit het korte label van het stuk: bij "9255 - OMWB V1" staat de
  // versie niet in het deel vóór " - " (3 nov: daar stond nog losse grijze tekst i.p.v. de badge).
  // Alleen uit de taaknaam (versieVanNaam), nooit uit een verrijkt label: daar kan een filmtitel
  // met "v2" in staan (Codex 3 nov).
  const versieVanTaak = (i) => versieVanNaam(taken[i].name);
  const versiesVan = (idx) => [...new Set(idx.map(versieVanTaak).filter(Boolean))];
  // Geen enkele versie in het blok (bv. "inspiratie"/"creatie" op 9252): niets te markeren; de
  // gewone onderregel blijft staan (3 nov: alleen v intern, v1, v2 … en DEF zijn mijlpalen).
  if (!groepen.some((g) => versiesVan(g.idx).length)) return [];
  let van = blok.c0;
  return groepen.map((g) => {
    const versies = versiesVan(g.idx);
    // Weekenddeadlines vallen op de werkdag ervoor (zelfde kolom als vrijdag): dan blijft elke
    // deadline apart benoemd in de hover/aria-tekst ("v1 af vr 9 okt · v2 af za 10 okt").
    const deadlines = [...new Map(g.idx.map((i) => {
      const t = taken[i];
      const naam = versieVanTaak(i) || labels[i];
      return [`${naam}|${eindIso(t)}`, { naam, eind: eindIso(t) }];
    })).values()].sort((a, b) => a.eind.localeCompare(b.eind));
    const seg = {
      deadlines,
      c0: van,
      c1: g.c1,
      // Eén stuk: het label is de versie (de onderregel links noemt de taak al).
      label: groepen.length === 1 ? versies.join(' + ') : labelVan(g),
      eind: g.eind,
      versie: versies.join(' + ') || null,
      hoofd: versies.some(isHoofdMijlpaal),
      badge: mijlpaalBadge(versies.join(' + ')),
      ruit: g.ruit,
      taken: g.idx.map((i) => taken[i]),
    };
    van = g.c1 + 1;
    return seg;
  });
}
